/*
 *  Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
 *
 *  Licensed under the Apache License, Version 2.0 (the "License").
 *  You may not use this file except in compliance with the License.
 *  A copy of the License is located at
 *
 *  http://aws.amazon.com/apache2.0
 *
 *  or in the "license" file accompanying this file. This file is distributed
 *  on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 *  express or implied. See the License for the specific language governing
 *  permissions and limitations under the License.
 */

import React from 'react';
import { Icon } from 'semantic-ui-react';

import {
  formatDuration,
  formatProjectedStop,
  idleStopGroup,
  IDLE_STOP_LOCKED_NOTICE,
} from '../../../helpers/schedule-display';

/**
 * The idle-stop timeout choice, the administrative lock, and the projected stop time.
 *
 * Presentational only: the current value arrives as a prop and every change is reported through
 * `onChange`. The Schedule_Modal owns the observable state, so this component holds none and does no
 * data fetching.
 *
 * The timeouts offered come entirely from the `options` prop, which the modal derives from
 * `idleStopOptions(schedulerIdleMinMinutes)`. This component never builds a ladder of its own, which
 * is what keeps the deployment floor honoured in one place (Requirement 7.2). It does own the off
 * option (Requirement 7.1), because removing the timeout deletes the tag rather than setting a
 * value, and that distinction belongs to the control that offers it rather than to the helper that
 * lists durations.
 *
 * The caveat that idleness is measured from CPU and GPU activity only, so a workspace being read from
 * without input may still count as idle (Requirement 7.6), is in the subtext the modal renders at the
 * top of the Idle Stop tab, not here. It moved there from a collapsed "How this works" panel, which
 * nobody opened, when that panel was replaced by section subtext.
 *
 * The CPU threshold sits beside the timeout on the same row, reading as one sentence: "Stop after
 * [1 hour] idle while CPU is below [8%]". The threshold half is one unit that wraps as a whole on a
 * narrow screen, so the sentence never breaks between a label and its control. A timeout means nothing without the activity level it is measured against,
 * and small Windows workspaces never went idle at the scheduler's fixed 3%. The recommended value for
 * the workspace's instance type is marked, and is what the modal opens on when no threshold is set.
 * The threshold row is hidden while the timeout is off, because it then does nothing. The admin lock
 * covers both: locked, both render as read-only values.
 *
 * There is still no control for idle tolerance or the GPU threshold. Tolerance keeps its deployment
 * default, and the GPU threshold is a deployment default the graph only draws.
 *
 * Props:
 * - minutes: number | null, the timeout currently set; null means no timeout
 * - options: [{ key, value, text }] from `idleStopOptions` in `schedule-display.js`
 * - canUserSet: boolean, false when the workspace configuration locks the value
 * - lockedMinutes: number | null, the value in force while locked
 * - warnedAt: string | null, the `IdleStopWarnedAt` timestamp; a warning is outstanding when present
 * - projectedStopAt: string | null, ISO 8601 stop time from the SchedulingInfo response
 * - timezone: `schedulerTimezone` from the SchedulingInfo response, e.g. 'Australia/Sydney'
 * - onChange: (minutesOrNull) => void
 * - cpuThreshold: number | null, the CPU threshold chosen, in percent
 * - cpuThresholdOptions: number[], the thresholds a user may choose
 * - recommendedCpuThreshold: number | null, marked "(recommended)" in the list
 * - onCpuThresholdChange: (percent) => void
 * - disabled: boolean
 */

// Select values are strings, so the off choice needs a sentinel that cannot collide with a number of
// minutes. It never leaves this component: `onChange` reports null for off, because deleting the tag
// is the absence of a value rather than a value of its own.
const OFF_VALUE = 'off';

const OFF_TEXT = 'Off';

const SELECT_ID = 'wsch-modal-idle-select';
const LABEL_ID = 'wsch-modal-idle-label';
const LABEL_TEXT = 'Stop after';

const THRESHOLD_SELECT_ID = 'wsch-modal-cpu-threshold-select';
const THRESHOLD_LABEL_ID = 'wsch-modal-cpu-threshold-label';
const THRESHOLD_LABEL_TEXT = 'idle while CPU is below';

/**
 * The threshold choices: the offered ones, plus the value currently set if it is not among them (set
 * outside Service Workbench), labelled the same way the timeout list labels one.
 */
function thresholdOptions(options, current, recommended) {
  const values = (options || []).filter(value => Number.isFinite(value));
  if (Number.isFinite(current) && !values.includes(current)) values.push(current);
  values.sort((a, b) => a - b);

  return values.map(value => {
    let text = `${value}%`;
    if (value === recommended) text += ' (recommended)';
    else if (!(options || []).includes(value)) text += ' (currently set)';
    return { key: value, value: String(value), text };
  });
}

/**
 * The option list for the timeout control.
 *
 * The supplied options are rendered as given, with the off choice first: the list reads from least
 * intervention to most. When the value currently set is not among them, the value is added as a
 * single extra option, labelled, so that a timeout written outside Service Workbench, or one written
 * before the deployment floor was raised, is shown as it actually is rather than as a blank control
 * or as some other timeout the user did not choose. That is the opposite of generating a ladder:
 * nothing is offered that is not either supplied or already set.
 */
function optionsIncludingValue(options, minutes) {
  const supplied = (options || []).filter(option => option && Number.isFinite(Number(option.value)));
  const offOption = { key: OFF_VALUE, value: OFF_VALUE, text: OFF_TEXT };

  if (!Number.isFinite(minutes)) return [offOption, ...supplied];
  if (supplied.some(option => Number(option.value) === minutes)) return [offOption, ...supplied];

  return [offOption, { key: minutes, value: minutes, text: `${formatDuration(minutes)} (currently set)` }, ...supplied];
}

/**
 * Splits the timeout list into the entries the dropdown renders: the off choice on its own at the top,
 * then the timeouts under Hours and Days headings, so a long list stays easy to scan. A value added as
 * currently set goes in the group its duration belongs to, in order, rather than at the top.
 *
 * @returns {Array<{ option: Object } | { group: string, options: Object[] }>}
 */
function groupedOptions(options) {
  const entries = [];
  const timeouts = options
    .filter(option => option.value !== OFF_VALUE)
    .sort((a, b) => Number(a.value) - Number(b.value));

  options.filter(option => option.value === OFF_VALUE).forEach(option => entries.push({ option }));
  timeouts.forEach(option => {
    const group = option.group || idleStopGroup(Number(option.value));
    const last = entries[entries.length - 1];
    if (last && last.group === group) last.options.push(option);
    else entries.push({ group, options: [option] });
  });

  return entries;
}

class WorkspaceIdleStopSection extends React.Component {
  // The locked value is the one the Idle_Policy reports as applicable, which is the configuration's
  // value rather than whatever the tag currently holds (Requirement 8.6). Falling back to `minutes`
  // keeps the row from reading as unset if the policy arrives without one.
  get effectiveMinutes() {
    const locked = this.props.canUserSet === false;
    const candidate =
      locked && Number.isFinite(this.props.lockedMinutes) ? this.props.lockedMinutes : this.props.minutes;

    return Number.isFinite(candidate) ? candidate : null;
  }

  get selectValue() {
    const minutes = this.effectiveMinutes;

    return minutes === null ? OFF_VALUE : String(minutes);
  }

  handleChange = event => {
    if (this.props.disabled) return;
    if (this.props.canUserSet === false) return;

    const onChange = this.props.onChange;
    if (!onChange) return;

    const raw = event.target.value;
    const next = raw === OFF_VALUE ? null : Number(raw);
    if (next !== null && !Number.isFinite(next)) return;
    if (next === this.effectiveMinutes) return;

    onChange(next);
  };

  handleThresholdChange = event => {
    if (this.props.disabled) return;
    if (this.props.canUserSet === false) return;

    const onChange = this.props.onCpuThresholdChange;
    if (!onChange) return;

    const next = Number(event.target.value);
    if (!Number.isFinite(next) || next === this.props.cpuThreshold) return;

    onChange(next);
  };

  renderThreshold(locked) {
    // Without a timeout the threshold does nothing, so it is not offered.
    if (this.effectiveMinutes === null) return null;

    const current = Number.isFinite(this.props.cpuThreshold) ? this.props.cpuThreshold : null;
    const options = thresholdOptions(this.props.cpuThresholdOptions, current, this.props.recommendedCpuThreshold);

    return (
      <span className="wsch-modal-idle-threshold" data-testid="idle-threshold-row">
        {locked ? (
          <span className="wsch-modal-idle-label" id={THRESHOLD_LABEL_ID}>
            {THRESHOLD_LABEL_TEXT}
          </span>
        ) : (
          <label className="wsch-modal-idle-label" htmlFor={THRESHOLD_SELECT_ID}>
            {THRESHOLD_LABEL_TEXT}
          </label>
        )}
        {locked ? (
          <span
            className="wsch-modal-idle-value wsch-modal-disabled"
            aria-disabled="true"
            aria-labelledby={THRESHOLD_LABEL_ID}
            data-testid="idle-threshold-locked-value"
          >
            {current === null ? 'the default' : `${current}%`}
          </span>
        ) : (
          <select
            id={THRESHOLD_SELECT_ID}
            className="wsch-modal-select"
            value={current === null ? '' : String(current)}
            disabled={!!this.props.disabled}
            onChange={this.handleThresholdChange}
            data-testid="idle-threshold-select"
          >
            {current === null && <option value="">Choose</option>}
            {options.map(option => (
              <option key={option.key} value={option.value}>
                {option.text}
              </option>
            ))}
          </select>
        )}
      </span>
    );
  }

  renderLockedValue() {
    const minutes = this.effectiveMinutes;

    // Rendered as a value rather than as a dimmed dropdown: a disabled dropdown still invites the
    // click that does nothing, whereas a read-only value shows what is in force and nothing else
    // (Requirement 8.7). It carries the shared disabled class and `aria-disabled` so that the state
    // is conveyed the same way as the modal's other non-native controls.
    return (
      <span
        className="wsch-modal-idle-value wsch-modal-disabled"
        aria-disabled="true"
        aria-labelledby={LABEL_ID}
        data-testid="idle-stop-locked-value"
      >
        {minutes === null ? OFF_TEXT : formatDuration(minutes)}
      </span>
    );
  }

  renderSelect() {
    const disabled = !!this.props.disabled;

    return (
      <select
        id={SELECT_ID}
        className="wsch-modal-select"
        value={this.selectValue}
        disabled={disabled}
        onChange={this.handleChange}
        data-testid="idle-stop-select"
      >
        {groupedOptions(optionsIncludingValue(this.props.options, this.effectiveMinutes)).map(entry =>
          entry.group ? (
            <optgroup key={entry.group} label={entry.group} data-testid={`idle-stop-group-${entry.group}`}>
              {entry.options.map(option => (
                <option key={option.key || option.value} value={option.value}>
                  {option.text}
                </option>
              ))}
            </optgroup>
          ) : (
            <option key={entry.option.key} value={entry.option.value}>
              {entry.option.text}
            </option>
          ),
        )}
      </select>
    );
  }

  renderLockedNotice() {
    return (
      <div className="wsch-modal-locked-notice" data-testid="idle-stop-locked-notice">
        <Icon name="lock" className="wsch-modal-locked-notice-icon" aria-hidden="true" />
        <div className="wsch-modal-locked-notice-text">{IDLE_STOP_LOCKED_NOTICE}</div>
      </div>
    );
  }

  /**
   * The projected stop time, shown only while a warning is outstanding (Requirement 7.7).
   *
   * The time is rendered by `formatProjectedStop` in the deployment timezone, never the browser's, so
   * that the user is not left to work out when the workspace will stop. The warning and the stop time
   * are separate advisory values derived from a reserved tag the External_Scheduler owns: when the
   * warning is present but the stop time is missing or unparseable, the warning is still reported,
   * without inventing a time for it.
   */
  renderProjectedStop() {
    const warnedAt = this.props.warnedAt;
    const outstanding = typeof warnedAt === 'string' && warnedAt.trim().length > 0;
    if (!outstanding) return null;

    const stopAt = formatProjectedStop(this.props.projectedStopAt, this.props.timezone);

    return (
      <div className="wsch-modal-projected-stop" data-testid="idle-stop-projected" aria-live="polite">
        <Icon name="clock outline" className="wsch-modal-projected-stop-icon" aria-hidden="true" />
        <div className="wsch-modal-projected-stop-text">
          {stopAt === null ? (
            'This workspace has been judged idle. It will stop unless it becomes busy again.'
          ) : (
            <>
              This workspace has been judged idle and is due to stop at{' '}
              <span className="wsch-modal-projected-stop-time">{stopAt}</span> unless it becomes busy again.
            </>
          )}
        </div>
      </div>
    );
  }

  /**
   * The row's label.
   *
   * A `label` is used only where there is a control for it to name. While the value is locked there
   * is no control, so the text becomes a plain span that the read-only value refers to through
   * `aria-labelledby`, rather than a label pointing at an element that is not in the document.
   */
  renderLabel(locked) {
    if (locked) {
      return (
        <span className="wsch-modal-idle-label" id={LABEL_ID}>
          {LABEL_TEXT}
        </span>
      );
    }

    return (
      <label className="wsch-modal-idle-label" htmlFor={SELECT_ID}>
        {LABEL_TEXT}
      </label>
    );
  }

  render() {
    const locked = this.props.canUserSet === false;

    return (
      <div data-testid="idle-stop-section">
        <div className="wsch-modal-idle-row">
          {this.renderLabel(locked)}
          {locked ? this.renderLockedValue() : this.renderSelect()}
          {this.renderThreshold(locked)}
        </div>
        {locked ? this.renderLockedNotice() : null}
        {this.renderProjectedStop()}
      </div>
    );
  }
}

export default WorkspaceIdleStopSection;
export { OFF_VALUE, thresholdOptions };
